/**
 * API client functions for the Project Config Git Repository settings.
 * Handles retrieving and saving the git repository that stores a project's
 * configuration files, along with the path layout used during deployment.
 */

import { apiClient } from '@/modules/api/api-client';

// --- Types ---

export interface ProjectConfigGitRepo {
  projectKey: string;
  repoUrl: string | null;
  /** CodeCommit repository name, used to list available versions via the CodeCommit API. */
  repoName: string | null;
  /** Branch/ref inspected when listing versions (defaults to "main"). */
  branch: string;
  checkoutLocation: string | null;
  currentPath: string;
  releasesPath: string;
  configurationSubPath: string;
  enabled: boolean;
}

// --- Config Git Repo APIs ---

/**
 * Retrieves the config git repository settings for a project.
 * Returns a default config (enabled=false, default paths) if none exists.
 */
export async function getConfigGitRepo(projectKey: string): Promise<ProjectConfigGitRepo> {
  return apiClient.get<ProjectConfigGitRepo>(
    `/projects/${encodeURIComponent(projectKey)}/config-git-repo`
  );
}

/**
 * Creates or updates the config git repository settings for a project.
 * Returns the normalised configuration persisted by the server.
 */
export async function saveConfigGitRepo(
  projectKey: string,
  config: ProjectConfigGitRepo
): Promise<ProjectConfigGitRepo> {
  return apiClient.put<ProjectConfigGitRepo>(
    `/projects/${encodeURIComponent(projectKey)}/config-git-repo`,
    config
  );
}
